import { CommonModule, Location } from '@angular/common';
import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { GetJobOfferCandidate } from 'src/app/models/job-offer/get-job-offer-candidate';
import { Resume } from 'src/app/models/resume/resume';
import { UserRole } from 'src/app/models/security/user-role-enum';
import { AuthService } from 'src/app/services/auth.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { JobApplicationService } from 'src/app/services/job-application.service';
import { JobOfferService } from 'src/app/services/job-offer.service';
import { ResumeService } from 'src/app/services/resume.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { ViewTrackerDirective } from 'src/app/directives/view-tracker.directive';
import { ViewType } from 'src/app/services/view-tracking.service';

@Component({
  selector: 'app-job-detail',
  imports: [FormsModule, CommonModule, ViewTrackerDirective],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.css'
})
export class JobDetailComponent {
 private readonly resumeService = inject(ResumeService);
  private readonly jobApplicationService = inject(JobApplicationService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly location = inject(Location);
  
  public jobOfferId = input<number | null>(null);
  jobOffer = signal<GetJobOfferCandidate>({
    id: 0,
    ownerUserId: null,
    ownerFullName: null,
    logoFileId: null,
    logoUrl: null,
    title: '',
    contractTypeId: null,
    contractTypeName: null,
    proposedSalary: null,
    statusId: null,
    statusName: null,
    description: null,
    responsabilities: null,
    requirenments: null,
    benefits: null,
    languagesRequired: null,
    travelDisponibilityId: null,
    travelDisponibilityName: null,
    needEmploymentAuthorization: null,
    educationLevelId: null,
    educationLevelName: null,
    workExperienceLevelId: null,
    workExperienceLevelName: null,
    contactInfoName: null,
    contactInfoEmail: null,
    contactInfoPhone: null,
    jobApplicationMethodId: null,
    jobApplicationMethodName: null,
    publicOn: null,
    closeOn: null,
    contractDurationId: null,
    contractDurationName: null,
    address: null,
    countryId: null,
    countryName: null,
    stateId: null,
    stateName: null,
    cityId: null,
    cityName: null,
    remoteWorkPossibilityId: null,
    remoteWorkPossibilityName: null,
    vacantPositions: null,
    workingTime: '',
    isEnabled: null,
    createUserId: null,
    createDate: null,
    modifyUserId: null,
    modifyDate: null,
    appliedOffer: false,
    departmentId: null,
    departmentName: null,
    positionId: null,
    positionName: null,
    skills: [],
    certifications: [],
    requirementsList: [],
    benefitsList: [],
    requiredLanguages: []
  });

  public resumes = signal<Resume[]>([]);
  public resumeId = signal<number | null>(null);

  ViewType = ViewType;

  selectedJobUrl: string = '';

  isLoading = signal<boolean>(false);

  constructor(private jobOfferService: JobOfferService,
        private docService: FileStorageService
  ) {
    effect(() => {
      if (this.jobOfferId()) {
        this.getJobOffers();
        this.getResumes();
      }
    })
  }

  getJobOffers() {
    const id = this.jobOfferId();
    if (!id) {
      return;
    }
    
    if (this.canUserApply()) {
      this.jobOfferService.getJobOffersCandidateById(id).subscribe({
        next: res => {
          if (res) {
            this.jobOffer.set({
              ...res,
              logoUrl: res.logoFileId ? this.docService.getFileUrl(`${res.logoFileId}`) : null
            });
          }
        }
      });
    } else {
      this.jobOfferService.getById(id).subscribe({
        next: res => {
          if (res) {
            const jobOfferData: GetJobOfferCandidate = {
              id: res.id,
              ownerUserId: res.ownerUserId,
              ownerFullName: res.ownerFullName,
              logoFileId: res.logoFileId,
              logoUrl:  res.logoFileId ? this.docService.getFileUrl(`${res.logoFileId}`) : null,
              title: res.title,
              contractTypeId: res.contractTypeId,
              contractTypeName: res.contractTypeName,
              proposedSalary: res.proposedSalary,
              statusId: res.statusId,
              statusName: res.statusName,
              description: res.description,
              responsabilities: res.responsabilities,
              requirenments: res.requirenments,
              benefits: res.benefits,
              languagesRequired: res.languagesRequired,
              travelDisponibilityId: res.travelDisponibilityId,
              travelDisponibilityName: res.travelDisponibilityName,
              needEmploymentAuthorization: res.needEmploymentAuthorization,
              educationLevelId: res.educationLevelId,
              educationLevelName: res.educationLevelName,
              workExperienceLevelId: res.workExperienceLevelId,
              workExperienceLevelName: res.workExperienceLevelName,
              contactInfoName: res.contactInfoName,
              contactInfoEmail: res.contactInfoEmail,
              contactInfoPhone: res.contactInfoPhone,
              jobApplicationMethodId: res.jobApplicationMethodId,
              jobApplicationMethodName: res.jobApplicationMethodName,
              publicOn: res.publicOn,
              closeOn: res.closeOn,
              contractDurationId: res.contractDurationId,
              contractDurationName: res.contractDurationName,
              address: res.address,
              countryId: res.countryId,
              countryName: res.countryName,
              stateId: res.stateId,
              stateName: res.stateName,
              cityId: res.cityId,
              cityName: res.cityName,
              remoteWorkPossibilityId: res.remoteWorkPossibilityId,
              remoteWorkPossibilityName: res.remoteWorkPossibilityName,
              vacantPositions: res.vacantPositions,
              workingTime: res.workingTime,
              isEnabled: res.isEnabled,
              createUserId: res.createUserId,
              createDate: res.createDate,
              modifyUserId: res.modifyUserId,
              modifyDate: res.modifyDate,
              skills: res.skills || [],
              certifications: res.certifications || [],
              requirementsList: res.requirementsList || [],
              benefitsList: res.benefitsList || [],
              requiredLanguages: res.requiredLanguages || [],
              appliedOffer: false,
              departmentId: res.departmentId,
              departmentName: res.departmentName,
              positionId: res.positionId,
              positionName: res.positionName
            };
            this.jobOffer.set(jobOfferData);
          }
        }
      });
    }
  }

  getResumes() {
    if (this.canUserApply()) {
      this.resumeService.getResumes().subscribe({
        next: res => {
          this.resumes.set(res);
        }
      });
    }
  }

  ngOnInit(): void {
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  isCandidate(): boolean {
    const userRole = this.authService.getUserRoleId();
    return userRole === UserRole.Member;
  }

  canUserApply(): boolean {
    return this.isLoggedIn() && this.isCandidate();
  }

  closeModal(){
    let divModal = document.getElementById('modalAplicarTrabajo');
    let modal = (window as any).bootstrap.Modal.getInstance(divModal);
    modal.hide();
  }

  goBack(){
    this.location.back();
  }

  submit() {
    // Verify the user can apply before proceeding
    if (!this.canUserApply() || this.jobOffer().appliedOffer) {
      return;
    }
    
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.isLoading.set(true);
    this.jobApplicationService.addJobApplication({
      jobApplicationId: null,
      jobId: this.jobOffer().id,
      resumeId: resumeId,
      statusId: 0,
      requestedByEmployeeId: null,
      recommendedByOperatorId: null,
      isEnabled: false
    }).subscribe({
      next: res => {
        this.isLoading.set(false);
        Swal.fire({
          title: '',
          text: 'Se aplicó a la oferta laboral',
          icon: 'success',
          confirmButtonColor: '#d33'
        });
        this.router.navigate(['/candidates/main-comparison']);
        this.closeModal();
      }, error: res => {
        this.isLoading.set(false);
        Swal.fire({
          title: 'Error',
          text: 'Ocurrió un error al aplicar a la oferta laboral.',
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
    });
  }

  beforeOpenShareModal(): void {
    // Safely get origin, fallback to empty string in SSR
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    this.selectedJobUrl = `${origin}/home/job/${this.jobOffer().id}`;
  }

  copyToClipboard(): void {
    // Check if clipboard API is available
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(this.selectedJobUrl).then(() => {
        alert('Enlace copiado al portapapeles');
      }).catch(() => {
        alert('Error al copiar el enlace');
      });
    } else {
      alert('La función de copiar no está disponible en este navegador');
    }
  }
}